import { buildWordTool, type WordTool } from './builder';

export const commentingTools: WordTool[] = [
  buildWordTool({
    name: 'get_comments',
    description:
      'Get all comments in the document with their replies, authors, and resolution status. Call this BEFORE manage_comment to get current comment IDs — IDs can change between sessions. Also call before add_comment to check existing comments. For adding new comments, use add_comment. For document text, use get_document. Returns array of comments with id, anchor_text, content, author, resolved status, and replies.',
    parameters: { type: 'object', properties: {} },
    getActivityDescription: () => 'Reading comments',
    execute: async () => {
      return await Word.run(async (context) => {
        const bodyRange = context.document.body.getRange('Whole');
        const comments = bodyRange.getComments();
        comments.load('items');
        await context.sync();

        if (comments.items.length === 0) {
          return JSON.stringify({
            comments: [],
            total: 0,
            hint: 'No comments in the document. Use add_comment to add one (call get_document first to find anchor text).',
          });
        }

        const ranges: Word.Range[] = [];
        for (const comment of comments.items) {
          comment.load('id,content,authorName,resolved');
          const range = comment.getRange();
          range.load('text');
          ranges.push(range);
          comment.replies.load('items');
        }
        await context.sync();

        for (const comment of comments.items) {
          for (const reply of comment.replies.items) {
            reply.load('content,authorName');
          }
        }
        await context.sync();

        const result = comments.items.map((comment, i) => {
          const base: Record<string, unknown> = {
            id: comment.id,
            anchor_text: ranges[i]!.text,
            comment: comment.content,
            author: comment.authorName,
            resolved: comment.resolved,
          };
          if (comment.replies.items.length > 0) {
            base.replies = comment.replies.items.map((r) => ({
              text: r.content,
              author: r.authorName,
            }));
          }
          return base;
        });

        return JSON.stringify({ comments: result, total: result.length });
      });
    },
  }),

  buildWordTool({
    name: 'add_comment',
    description:
      'Add a comment anchored to specific text in the document body. PREREQUISITE: Call get_document first to find exact anchor text (5-15 unique words from the target sentence). The anchor text is searched in the document body and the comment is attached to the first match. Cannot attach comments to text in headers, footers, or tables — body text only. Returns the anchor preview, comment text, and match count. Warns if multiple matches found.',
    parameters: {
      type: 'object',
      properties: {
        anchor: {
          type: 'string',
          description:
            'Exact text from the document to anchor the comment to. Must match existing text in the document body. Use 5-15 unique words from the target sentence. Get the exact text from get_document first — even small differences in spacing or punctuation cause "not found" errors.',
        },
        comment: {
          type: 'string',
          description:
            'The comment text to attach. Write actionable comments that say WHAT to change and WHY.',
        },
        match_case: {
          type: 'boolean',
          description: 'Set to true for case-sensitive anchor matching. Defaults to false.',
        },
      },
      required: ['anchor', 'comment'],
    },
    permission: true,
    validateInput: (args) => {
      const anchor = args.anchor as string;
      const commentText = args.comment as string;
      if (!anchor || anchor.trim().length === 0) {
        return {
          error: 'anchor text is empty.',
          recovery:
            'Provide text from the document to anchor the comment to. Use get_document to find exact text.',
        };
      }
      if (!commentText || commentText.trim().length === 0) {
        return { error: 'comment text is empty.', recovery: 'Provide the comment content.' };
      }
      return null;
    },
    getActivityDescription: (args) => {
      const a =
        typeof args.anchor === 'string' && args.anchor.length > 40
          ? args.anchor.slice(0, 40) + '…'
          : args.anchor;
      return `Adding comment on "${a}"`;
    },
    execute: async (args) => {
      const anchor = args.anchor as string;
      const commentText = args.comment as string;
      return await Word.run(async (context) => {
        const matchCase = args.match_case === true;

        const results = context.document.body.search(anchor, { matchCase });
        results.load('items');
        await context.sync();

        if (results.items.length === 0) {
          return JSON.stringify({
            error: `Text "${anchor.length > 60 ? anchor.slice(0, 60) + '...' : anchor}" not found in the document.`,
            recovery:
              'Use get_document to check the exact text. Even small differences in spacing or punctuation cause mismatches. The search only covers body text, not headers/footers/tables.',
          });
        }

        const created = results.items[0]!.insertComment(commentText);
        created.load('id');
        await context.sync();

        const anchorPreview = anchor.length > 60 ? anchor.slice(0, 60) + '...' : anchor;
        return JSON.stringify({
          success: true,
          comment_id: created.id,
          anchor: anchorPreview,
          comment: commentText,
          matches_found: results.items.length,
          hint:
            results.items.length > 1
              ? `Warning: ${results.items.length} matches found for this anchor text. Comment was attached to the first match. Use a longer, more unique anchor to target a specific location.`
              : undefined,
        });
      });
    },
  }),

  buildWordTool({
    name: 'manage_comment',
    description:
      "Manage an existing comment. PREREQUISITE: call get_comments first. STANDARD CLOSURE PATTERN after fixing an issue flagged by a reviewer: use action='resolve_with_reply' with text describing what you did — that's ONE atomic call that replies AND resolves. Only use action='delete' when the user explicitly asks to REMOVE comments — never to 'close' a fixed one (delete erases the audit trail). Other actions: 'resolve'/'unresolve' change status; 'reply' adds a reply without resolving; 'edit' rewrites the original comment text.",
    parameters: {
      type: 'object',
      properties: {
        comment_id: {
          type: 'string',
          description:
            'The comment ID from get_comments. Always fetch fresh IDs before calling this — IDs change between sessions.',
        },
        action: {
          type: 'string',
          enum: ['resolve_with_reply', 'edit', 'resolve', 'unresolve', 'reply', 'delete'],
          description:
            "USE 'resolve_with_reply' as the default closure after fixing a reviewer's issue — it atomically adds a reply AND marks resolved in one call (requires text describing what was done). Do NOT split into 'reply' + 'resolve'; that's legacy and the agent often forgets one half. 'unresolve' reopens a resolved comment. 'reply' is ONLY for when you CANNOT resolve yet (e.g., asking the reviewer for clarification — leaves the comment open). 'edit' rewrites the original comment (rare — you usually don't own that text). 'delete' permanently removes the comment AND all replies; use only when the user explicitly asks.",
        },
        text: {
          type: 'string',
          description:
            "Required for 'resolve_with_reply', 'reply', and 'edit'. Describe what you did (e.g., \"Replaced 'utilize' with 'use' in paragraphs 2, 5, 8.\"). Ignored for resolve/unresolve/delete.",
        },
      },
      required: ['comment_id', 'action'],
    },
    permission: true,
    isDestructive: (args) => args.action === 'delete' || args.action === 'edit',
    validateInput: (args) => {
      const action = args.action as string;
      if (
        (action === 'edit' || action === 'reply' || action === 'resolve_with_reply') &&
        typeof args.text !== 'string'
      ) {
        return {
          error: `'text' is required when action is '${action}'.`,
          recovery:
            action === 'edit'
              ? 'Provide the new comment content.'
              : 'Provide a short reply describing what was done.',
        };
      }
      return null;
    },
    getActivityDescription: (args) => `Comment ${args.action}: ${args.comment_id}`,
    execute: async (args) => {
      const action = args.action as string;
      const commentId = args.comment_id as string;

      return await Word.run(async (context) => {
        const bodyRange = context.document.body.getRange('Whole');
        const comments = bodyRange.getComments();
        comments.load('items');
        await context.sync();

        for (const c of comments.items) {
          c.load('id');
        }
        await context.sync();

        const comment = comments.items.find((c) => c.id === commentId);
        if (!comment) {
          return JSON.stringify({
            error: `Comment with ID '${commentId}' not found.`,
            recovery: 'Comment IDs may have changed. Call get_comments to get current IDs and retry.',
          });
        }

        switch (action) {
          case 'edit':
            comment.content = args.text as string;
            break;
          case 'resolve':
            comment.resolved = true;
            break;
          case 'unresolve':
            comment.resolved = false;
            break;
          case 'reply':
            comment.reply(args.text as string);
            break;
          case 'resolve_with_reply':
            comment.reply(args.text as string);
            comment.resolved = true;
            break;
          case 'delete':
            comment.delete();
            break;
        }
        await context.sync();

        const textPreview = typeof args.text === 'string' ? args.text.slice(0, 60) : '';
        const detail =
          action === 'edit'
            ? `Comment updated to: "${textPreview}"`
            : action === 'resolve'
              ? 'Comment marked as resolved.'
              : action === 'unresolve'
                ? 'Comment reopened.'
                : action === 'reply'
                  ? `Reply added: "${textPreview}"`
                  : action === 'resolve_with_reply'
                    ? `Reply added and comment resolved: "${textPreview}"`
                    : 'Comment deleted permanently.';

        return JSON.stringify({
          success: true,
          comment_id: commentId,
          action,
          detail,
        });
      });
    },
  }),
];
